import Phaser from 'phaser';
import * as ABCya from '@abcya/abcya_phaser';
import Title from '../01 Title/Title';
import ImageKeys from '../UIAssetKeys';
import ShapeReview from '../02 ShapeReview/ShapeReview';
import BeachRegion from '../03 Beach/BeachRegion';
import { GameData, loadGameData, REGIONS } from '../gameData';
import MenuPopup from '../06 MenuPopup/MenuPopup';
import DowntownRegion from '../07 Downtown/DowntownRegion';
import CampRegion from '../09 Camping/CampRegion';
import SpaceRegion from '../11 Space/SpaceRegion';
import CompletionScreen from '../13 Completion/CompletionScreen';

/**
 * This is your first scene.
 */
export default class Game extends Phaser.Scene
{
    static get KEY() { return 'Game'; }

    init()
    {
        // load previous game data
        //localStorage.clear();
        loadGameData();
    }

    preload()
    {

    }

    create()
    {
        let frame = this.textures.getFrame(ABCya.Images.DEFAULT_ATLAS, ImageKeys._13_Completion_Print___Black_and_White_FinishedBnW);
        this.gameRt = new Phaser.GameObjects.RenderTexture(this, 0, 0, frame.width, frame.height)

        this.events.on(GameEvents.GO_TO_STATE, this.goToState, this);
        this.events.on(GameEvents.GO_TO_NEXT_STATE, this.goToNextState, this);
        this.events.on(GameEvents.SHOW_MENU, this.showMenu, this);

        this.background = ABCya.Images.addImage(0, 0, ImageKeys._01_Title_Screen_BG, this).setOrigin(0);

        this._currentState = GameStates.STARTUP;

        this.titleScreen = new Title(this);

        this.shapeReviewScreen = new ShapeReview(this);

        this.beachRegion = new BeachRegion(this);
        this.downtownRegion = new DowntownRegion(this);
        this.campRegion = new CampRegion(this);
        this.spaceRegion = new SpaceRegion(this);

        this.menuPopup = new MenuPopup(this);

        this.completionScreen = new CompletionScreen(this);

        // set which screen to go to (normally should be title screen)
        this.goToState(GameStates.TITLE);
        //this.goToState(REGIONS.BEACH);
        //this.goToState(REGIONS.DOWNTOWN);
        //this.goToState(REGIONS.CAMP);
        //this.goToState(REGIONS.SPACE);
        //this.goToState(GameStates.COMPLETION);

        this.events.emit(GameEvents.TOGGLE_SOUND, true);

        //ABCya.Debug.enableImagePlacerDebug(this);
    }

    /**
     * Goes to the next state, based on the current state. Only works when within a region state.
     */
    goToNextState()
    {
        if (this._currentState == REGIONS.BEACH)
        {
            this.goToState(REGIONS.DOWNTOWN);
        }
        else if (this._currentState == REGIONS.DOWNTOWN)
        {
            this.goToState(REGIONS.CAMP);
        }
        else if (this._currentState == REGIONS.CAMP)
        {
            this.goToState(REGIONS.SPACE);
        }
    }

    /**
     * Goes to the specified GameState. Does nothing if the current state is already that state.
     * This function returns once the new state is active.
     * @param {string} state the GameState enum value to go to
     */
    async goToState(state)
    {
        if (this._currentState == state) return;

        this.prepareNextState(state);

        await this.leaveCurrentState(state);

        let previousState = this._currentState;
        this._currentState = state;

        await this.enterCurrentState();

        this.resetPreviousState(previousState);
    }

    /**
     * Prepares the next state.
     * @param {string} nextState the GameState enum value
     */
    prepareNextState(nextState)
    {
        switch (nextState)
        {
            case GameStates.TITLE:
                this.titleScreen.prepare();
                break;
            case GameStates.SHAPE_REVIEW:
                this.shapeReviewScreen.prepare();
                break;
            case REGIONS.BEACH:
                this.beachRegion.prepare();
                break;
            case REGIONS.DOWNTOWN:
                this.downtownRegion.prepare();
                break;
            case REGIONS.CAMP:
                this.campRegion.prepare();
                break;
            case REGIONS.SPACE:
                this.spaceRegion.prepare();
                break;
            case GameStates.COMPLETION:
                this.completionScreen.prepare();
                break;
        }
    }

    /**
     * Enters the current state. This function returns once the enter animation has completed for that state.
     */
    async enterCurrentState()
    {
        switch (this._currentState)
        {
            case GameStates.TITLE:
                await this.titleScreen.begin();
                break;
            case GameStates.SHAPE_REVIEW:
                await this.shapeReviewScreen.begin();
                break;
            case REGIONS.BEACH:
                await this.beachRegion.begin();
                break;
            case REGIONS.DOWNTOWN:
                await this.downtownRegion.begin();
                break;
            case REGIONS.CAMP:
                await this.campRegion.begin();
                break;
            case REGIONS.SPACE:
                await this.spaceRegion.begin();
                break;
            case GameStates.COMPLETION:
                await this.completionScreen.begin();
                break;
        }
    }

    /**
     * Leaves the current state. This function returns once the leave animation has completed for that state.
     * @param {string} nextState the GameState enum value to go to
     */
    async leaveCurrentState(nextState)
    {
        switch (this._currentState)
        {
            case GameStates.TITLE:
                await this.titleScreen.leave();
                break;
            case GameStates.SHAPE_REVIEW:
                await this.shapeReviewScreen.leave(nextState);
                break;
            case REGIONS.BEACH:
                await this.beachRegion.leave();
                break;
            case REGIONS.DOWNTOWN:
                await this.downtownRegion.leave();
                break;
            case REGIONS.CAMP:
                await this.campRegion.leave();
                break;
            case REGIONS.SPACE:
                await this.spaceRegion.leave();
                break;
            case GameStates.COMPLETION:
                await this.completionScreen.leave();
                break;
        }
    }

    /**
     * Resets the previous state.
     * @param {string} previousState the GameState enum value
     */
    resetPreviousState(previousState)
    {
        switch (previousState)
        {
            case GameStates.TITLE:
                this.titleScreen.reset();
                break;
            case GameStates.SHAPE_REVIEW:
                this.shapeReviewScreen.reset();
                break;
            case REGIONS.BEACH:
                this.beachRegion.reset();
                break;
            case REGIONS.DOWNTOWN:
                this.downtownRegion.reset();
                break;
            case REGIONS.CAMP:
                this.campRegion.reset();
                break;
            case REGIONS.SPACE:
                this.spaceRegion.reset();
                break;
            case GameStates.COMPLETION:
                this.completionScreen.reset();
                break;
        }
    }

    /**
     * Shows the menu popup screen.
     */
    showMenu()
    {
        this.menuPopup.prepare();
        this.menuPopup.enter();
    }
}

/**
 * The GameStates. Additional states are each of the REGIONS enum values.
 */
export const GameStates = {
    STARTUP: "Startup",
    TITLE: "Title",
    SHAPE_REVIEW: "ShapeReview",
    COMPLETION: "Completion",
}

/**
 * Global game events.
 */
export const GameEvents = {
    /**
     * First parameter is the state to go to (either a GameState enum or a REGIONS enum).
     */
    GO_TO_STATE: "gotostate",
    /**
     * First parameter is a Boolean indiciating whether the sound should be enabled or disabled (muted).
     */
    TOGGLE_SOUND: "togglesound",
    SHOW_MENU: "showmenu",
    /**
     * Will send the game to the next state - to be used for region switch button
     */
    GO_TO_NEXT_STATE: "gotonextstate",
}